import type { Item } from '@cheqroom/api';

export type GetCheckoutQuantitiesItem = Pick<Item, 'kitQuantities' | 'orderQuantitiesWithKits'> & {
	kit?: Pick<NonNullable<Item['kit']>, '_id'>;
};

export interface CheckoutQuantities {
	/** How much of this bulk item is allocated to `item.kit`, if any. */
	kitQuantity: number;
	/** Sum of every order against `item.kit` — not just the first one. Used to tell whether that
	 * kit's own allocation is fully checked out. */
	checkedOutKitQuantity: number;
	/** Sum of every order with no `kitId` — the item's own standalone stock, separate from any
	 * kit's allocation. Used for "how much of the loose stock is available" wording. */
	checkedOutLooseQuantity: number;
	/** Sum of every order regardless of `kitId` — used to tell whether the item is fully checked
	 * out *everywhere* (loose stock and every kit combined), not just within one kit's context. */
	totalCheckedOutQuantity: number;
}

/**
 * Shared by `StatusContent`'s icon-color check and `getStatusText`'s wording — kept in one place
 * after the two independently duplicated this aggregation and drifted apart (one summed every
 * matching order, the other only looked at the first one).
 */
export const getCheckoutQuantities = (item: GetCheckoutQuantitiesItem): CheckoutQuantities => {
	const orders = item.orderQuantitiesWithKits ?? [];
	const kitQuantity = item.kit && item.kitQuantities ? (item.kitQuantities[item.kit._id] ?? 0) : 0;

	const checkedOutKitQuantity = orders
		.filter(({ kitId }) => kitId === item.kit?._id)
		.reduce((acc, curr) => acc + curr.quantity, 0);

	const checkedOutLooseQuantity = orders
		.filter(({ kitId }) => kitId == null)
		.reduce((acc, curr) => acc + curr.quantity, 0);

	const totalCheckedOutQuantity = orders.reduce((acc, curr) => acc + curr.quantity, 0);

	return { kitQuantity, checkedOutKitQuantity, checkedOutLooseQuantity, totalCheckedOutQuantity };
};
